// Phase 6.0 · HOME · STEP UX-2 (2026-09-10 판매자 지시 §2·§3·§4·§5·§7)
// · "지금 LIVE" 를 실제 external LIVE (네이버 + 카카오) 로 대체.
// · Common HomeLiveCard 사용 · platform interleave 는 백엔드 처리.
// · "전체 LIVE 보기" 는 편성표(/schedule) 가 아니라 LIVE 전용
//   `/live/all` 로 이동 (§7 재정의).
// · 카드 클릭은 LIVEMOA Watch Hub (`/live/hub/{external_id}`) 로 라우팅
//   (§20 권장 · §17 iframe 금지).  기존 internal LiveKit 경로 (`/lives/now`
//   → LiveCard → `/live-view/...`) 는 TodayScheduleList 등 다른 섹션에서
//   여전히 사용됨 (§0 LOCK).
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, TrendingUp, Star } from 'lucide-react';
import api from '@/lib/api';
import SellerCard from '@/components/SellerCard';
import HomeLiveCard from '@/components/HomeLiveCard';
import TodayScheduleList from '@/components/TodayScheduleList';
import SmallCategoryStrip from '@/components/home/SmallCategoryStrip';
import PlatformIntroCard from '@/components/home/PlatformIntroCard';
import HomeHeroSlider from '@/components/home/HomeHeroSlider';
import ShortClipStrip from '@/components/home/ShortClipStrip';
import PopularGroupBuys from '@/components/home/PopularGroupBuys';
import ThemedProducts from '@/components/home/ThemedProducts';

const Home = () => {
  const [liveNow, setLiveNow] = useState([]);
  const [liveTotal, setLiveTotal] = useState(0);
  const [popularSellers, setPopularSellers] = useState([]);
  const [newSellers, setNewSellers] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [liveRes, popularRes, newRes, categoriesRes] = await Promise.all([
          // STEP UX-2 · Home 초기 노출 6개 (§3) · platform interleave
          // 는 백엔드 `/live/live-now` 가 담당 (§4).
          api.get('/live/live-now', { params: { limit: 6 } }),
          api.get('/public/home/popular-sellers?limit=6'),
          api.get('/sellers/new'),
          api.get('/categories'),
        ]);
        setLiveNow(liveRes.data?.items || []);
        setLiveTotal(liveRes.data?.total || 0);
        setPopularSellers(popularRes.data?.items || popularRes.data || []);
        setNewSellers(newRes.data || []);
        const cats = Array.isArray(categoriesRes.data) ? categoriesRes.data : [];
        const active = cats
          .filter((c) => c.is_home_active !== false)
          .sort((a, b) => (a.order || 0) - (b.order || 0));
        setCategories(active);
      } catch (e) { console.error('home fetch failed:', e); }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A]" data-testid="home-page-v6">
      {/* ─── 상단 (STEP A/B) · 유지 ───────────────────────────────── */}
      <SmallCategoryStrip categories={categories} />
      <PlatformIntroCard />
      <HomeHeroSlider />
      <ShortClipStrip />

      {/* ─── 하단 (STEP C) · 순서 정렬 ────────────────────────────── */}

      {/* ① 지금 LIVE (STEP UX-2 · 실 external LIVE 데이터) */}
      {liveNow.length > 0 && (
        <section className="py-8 sm:py-10 border-b border-black/5 dark:border-white/10" data-testid="home-live-now">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-baseline gap-2">
                <h2 className="text-xl sm:text-2xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="live-now-title">
                  🔴 지금 LIVE
                </h2>
                {liveTotal > 0 && (
                  <span className="text-[11px] sm:text-xs text-gray-500 dark:text-gray-400" data-testid="live-now-total">
                    {liveTotal}건
                  </span>
                )}
              </div>
              {/* §7 · 전체 편성표 (/schedule) 대신 LIVE 전용 (/live/all) 로 이동. */}
              <Link to="/live/all" className="text-xs sm:text-sm font-bold text-[#FF3B30] hover:underline flex items-center gap-0.5" data-testid="view-all-live">
                전체 LIVE 보기 <ChevronRight size={14} />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4" data-testid="home-live-grid">
              {liveNow.slice(0, 6).map((live) => (
                <HomeLiveCard key={live.external_id} live={live} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ② 오늘 방송 예정 · 기존 편성표 컴포넌트 재사용 */}
      <TodayScheduleList limit={30} />

      {/* ③ 인기 공동구매 · Phase 5.9 group_buys 재사용 */}
      <PopularGroupBuys />

      {/* ④ 기획상품/테마 추천 · seller_products 재사용 */}
      <ThemedProducts />

      {/* ⑤ 지금 뜨는 판매자 */}
      {popularSellers.length > 0 && (
        <section className="py-8 sm:py-10 border-b border-black/5 dark:border-white/10" data-testid="home-popular-sellers">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <TrendingUp className="text-[#FF3B30]" size={20} />
                <h2 className="text-xl sm:text-2xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="popular-sellers-title">
                  ❤️ 지금 뜨는 판매자
                </h2>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {popularSellers.slice(0, 6).map((s) => (
                <SellerCard key={s.seller_id} seller={s} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ⑥ NEW SELLER */}
      {newSellers.length > 0 && (
        <section className="py-8 sm:py-10" data-testid="home-new-sellers">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Star className="text-yellow-500" size={20} />
                <h2 className="text-xl sm:text-2xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="new-sellers-title">
                  🆕 NEW SELLER
                </h2>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {newSellers.slice(0, 6).map((s) => (
                <SellerCard key={s.seller_id} seller={s} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default Home;
